import React, { useEffect, useState } from "react";
import { Monitor, FolderOpen, Info, Copy, Check, Loader2, AlertTriangle, RefreshCw, Download } from "lucide-react";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import { Switch } from "@/components/ui/switch";

/**
 * DesktopSettings — §36's General/Advanced/About sections, scoped to what's
 * actually specific to running as a Windows app (launch at sign-in, tray
 * behavior, notifications, diagnostics). Everything else in §36
 * (Appearance, Migration defaults, Notification routing) already has a
 * real home elsewhere in the app and applies identically on web — nothing
 * to duplicate here.
 *
 * Pure client-side preferences via window.moovbyte's IPC bridge (desktop/
 * main.js) -- no backend calls, nothing to load/save against a server.
 * Route only exists when running in Electron (see Layout.jsx's SETTINGS_GROUP
 * and App.js), so window.moovbyte is always present here.
 */
export default function DesktopSettings() {
    const [launchAtLogin, setLaunchAtLoginState] = useState(true);
    const [minimizeToTray, setMinimizeToTrayState] = useState(false);
    const [closeToTray, setCloseToTrayState] = useState(true);
    const [showNotifications, setShowNotificationsState] = useState(true);
    const [version, setVersion] = useState("");
    const [loaded, setLoaded] = useState(false);
    const [loadError, setLoadError] = useState(false);

    useEffect(() => {
        Promise.all([
            window.moovbyte.getLaunchAtLogin(),
            window.moovbyte.getPreference("minimizeToTray", false),
            window.moovbyte.getPreference("closeToTray", true),
            window.moovbyte.getPreference("showNotifications", true),
            window.moovbyte.getVersion(),
        ]).then(([launch, minTray, closeTray, notif, ver]) => {
            setLaunchAtLoginState(launch);
            setMinimizeToTrayState(minTray);
            setCloseToTrayState(closeTray);
            setShowNotificationsState(notif);
            setVersion(ver);
            setLoaded(true);
        }).catch(() => {
            setLoadError(true);
        });
    }, []);

    const toggleLaunchAtLogin = async (v) => {
        setLaunchAtLoginState(v);
        await window.moovbyte.setLaunchAtLogin(v);
        toast.success(v ? "MoovBytes will launch when you sign in" : "Launch at sign-in turned off");
    };
    const toggleMinimizeToTray = async (v) => {
        setMinimizeToTrayState(v);
        await window.moovbyte.setPreference("minimizeToTray", v);
    };
    const toggleCloseToTray = async (v) => {
        setCloseToTrayState(v);
        await window.moovbyte.setPreference("closeToTray", v);
    };
    const toggleNotifications = async (v) => {
        setShowNotificationsState(v);
        await window.moovbyte.setPreference("showNotifications", v);
    };

    if (loadError) {
        return (
            <div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center">
                <AlertTriangle size={22} className="text-amber-400" />
                <p className="text-sm text-muted-foreground">
                    Couldn&apos;t load desktop preferences. Try restarting MoovBytes.
                </p>
            </div>
        );
    }

    if (!loaded) {
        return (
            <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
                <Loader2 size={14} className="animate-spin" /> Loading…
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-3xl space-y-5 p-6">
            <PageHero
                eyebrow="Windows Application"
                title="Desktop Settings"
                description="Preferences specific to running MoovBytes as a Windows app — launch behavior, the system tray, and notifications."
                icon={Monitor}
                tint="#5b5ef0"
                testId="desktop-settings-hero"
            />

            <SettingsCard title="General">
                <SettingRow
                    label="Launch MoovBytes when I sign in to Windows"
                    checked={launchAtLogin}
                    onChange={toggleLaunchAtLogin}
                    testid="desktop-settings-launch-at-login"
                />
                <SettingRow
                    label="Minimize to tray"
                    description="Clicking the minimize button hides the window to the system tray instead of the taskbar."
                    checked={minimizeToTray}
                    onChange={toggleMinimizeToTray}
                    testid="desktop-settings-minimize-to-tray"
                />
                <SettingRow
                    label="Keep running in the tray when closed"
                    description="Off: clicking the close button exits MoovBytes entirely. Either way, closing the window never cancels an in-progress migration."
                    checked={closeToTray}
                    onChange={toggleCloseToTray}
                    testid="desktop-settings-close-to-tray"
                />
            </SettingsCard>

            <SettingsCard title="Notifications">
                <SettingRow
                    label="Show Windows notifications"
                    description="Migration completed, failed, or needs attention."
                    checked={showNotifications}
                    onChange={toggleNotifications}
                    testid="desktop-settings-notifications"
                />
            </SettingsCard>

            <UpdateCard version={version} />

            <DiagnosticsCard version={version} />
        </div>
    );
}

function SettingsCard({ title, children }) {
    return (
        <div className="overflow-hidden rounded-xl border border-border bg-surface">
            <div className="border-b border-border px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {title}
            </div>
            <div className="divide-y divide-border/60">{children}</div>
        </div>
    );
}

function SettingRow({ label, description, checked, onChange, testid }) {
    return (
        <label className="flex cursor-pointer items-start justify-between gap-4 px-4 py-3.5">
            <div className="min-w-0">
                <div className="text-sm font-medium">{label}</div>
                {description && <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>}
            </div>
            <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} className="mt-0.5 shrink-0" />
        </label>
    );
}

// Auto-update status (desktop/updater.js). Checks run in the background
// automatically on a timer; this card is for visibility + a manual
// "Check Now" rather than the primary mechanism -- most users never need
// to open this, updates just apply on their own.
function UpdateCard({ version }) {
    const [status, setStatus] = useState(null);
    const [checking, setChecking] = useState(false);

    useEffect(() => {
        window.moovbyte.updater.getStatus().then(setStatus).catch(() => {});
        return window.moovbyte.updater.onStatus(setStatus);
    }, []);

    const checkNow = async () => {
        setChecking(true);
        try {
            await window.moovbyte.updater.checkNow();
        } finally {
            // A real result arrives async via onStatus; this just bounds
            // how long the button shows a spinner if the check hangs.
            setTimeout(() => setChecking(false), 4000);
        }
    };

    const restartNow = () => window.moovbyte.updater.quitAndInstall();

    const state = status?.state;
    const label = {
        idle: "Up to date",
        checking: "Checking for updates…",
        "not-available": "Up to date",
        available: `Downloading update ${status?.version || ""}…`,
        downloading: `Downloading update${status?.percent != null ? ` (${status.percent}%)` : ""}…`,
        downloaded: `Update ${status?.version || ""} ready to install`,
        error: "Couldn't check for updates",
    }[state] || "Up to date";

    return (
        <SettingsCard title="Software Update">
            <div className="flex items-center justify-between px-4 py-3.5">
                <div className="min-w-0">
                    <div className="text-sm font-medium">{label}</div>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                        {state === "downloaded"
                            ? "Restart MoovBytes to finish installing."
                            : `Current version v${version}. MoovBytes checks for updates automatically.`}
                    </p>
                </div>
                {state === "downloaded" ? (
                    <button
                        type="button"
                        onClick={restartNow}
                        data-testid="desktop-settings-restart-update"
                        className="inline-flex shrink-0 items-center gap-1.5 rounded border border-[#5b5ef0]/50 bg-[#5b5ef0]/10 px-2.5 py-1 text-xs text-[#a5a8ff] hover:bg-[#5b5ef0]/20"
                    >
                        <Download size={12} /> Restart & Install
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={checkNow}
                        disabled={checking || state === "checking" || state === "downloading"}
                        data-testid="desktop-settings-check-updates"
                        className="inline-flex shrink-0 items-center gap-1.5 rounded border border-border px-2.5 py-1 text-xs text-muted-foreground hover:border-foreground/40 hover:text-foreground disabled:opacity-50"
                    >
                        <RefreshCw size={12} className={checking || state === "checking" ? "animate-spin" : ""} />
                        Check Now
                    </button>
                )}
            </div>
        </SettingsCard>
    );
}

// §58 Diagnostics — safe, copyable info only. Never a credential/token in
// sight, matching the spec's own explicit "never expose credentials" rule.
function DiagnosticsCard({ version }) {
    const [copied, setCopied] = useState(false);

    const diagnosticsText = `MoovBytes Desktop\nVersion ${version}\nPlatform: Windows`;

    const copyDiagnostics = () => {
        window.moovbyte.writeClipboardText(diagnosticsText);
        setCopied(true);
        toast.success("Diagnostics copied");
        setTimeout(() => setCopied(false), 1600);
    };

    const openLogFolder = () => window.moovbyte.openLogFolder();

    return (
        <SettingsCard title="About & Diagnostics">
            <div className="flex items-center justify-between px-4 py-3.5">
                <div className="flex items-center gap-2 text-sm">
                    <Info size={14} className="text-muted-foreground" />
                    MoovBytes Desktop
                </div>
                <div className="font-mono text-xs text-muted-foreground" data-testid="desktop-settings-version">
                    v{version}
                </div>
            </div>
            <div className="flex items-center justify-between px-4 py-3.5">
                <span className="text-sm">Diagnostics</span>
                <button
                    type="button"
                    onClick={copyDiagnostics}
                    data-testid="desktop-settings-copy-diagnostics"
                    className="inline-flex items-center gap-1.5 rounded border border-border px-2.5 py-1 text-xs text-muted-foreground hover:border-foreground/40 hover:text-foreground"
                >
                    {copied ? <Check size={12} /> : <Copy size={12} />}
                    {copied ? "Copied" : "Copy Diagnostics"}
                </button>
            </div>
            <div className="flex items-center justify-between px-4 py-3.5">
                <span className="text-sm">Log folder</span>
                <button
                    type="button"
                    onClick={openLogFolder}
                    data-testid="desktop-settings-open-log-folder"
                    className="inline-flex items-center gap-1.5 rounded border border-border px-2.5 py-1 text-xs text-muted-foreground hover:border-foreground/40 hover:text-foreground"
                >
                    <FolderOpen size={12} /> Open Log Folder
                </button>
            </div>
        </SettingsCard>
    );
}
